import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

interface Enrolment {
  id: string;
  candidate_name: string;
  programme_type: string;
  package: string;
  status: string;
  current_phase: string;
  current_session: number;
  start_date: string;
}

const PHASE_LABELS: Record<string, string> = {
  S: 'See Yourself', P: 'Plant Roots', A: 'Arm Yourself',
  R: 'Rise', K: 'Keep Flame', E1: 'Explore 1', E2: 'Explore 2', D: 'Decide',
};

export function TrainerDashboard() {
  usePageTitle('Trainer Dashboard');
  const [clients, setClients] = useState<Enrolment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Enrolment[]>('/programmes')
      .then(setClients)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const active = clients.filter((c) => c.status === 'active');
  const completed = clients.filter((c) => c.status === 'completed');

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
          Trainer Dashboard
        </h1>
        <p className="text-mustard-charcoal/60 mb-8">
          {active.length} active client{active.length !== 1 ? 's' : ''} &middot; {completed.length} completed
        </p>

        {loading ? (
          <p className="text-mustard-charcoal/60">Loading clients...</p>
        ) : active.length === 0 && completed.length === 0 ? (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <p className="text-mustard-charcoal/60">No clients assigned yet.</p>
          </div>
        ) : (
          <>
            {/* Active clients */}
            {active.length > 0 && (
              <div className="mb-10">
                <h2 className="font-display text-xl font-bold text-mustard-green-dark mb-4">Active Clients</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {active.map((client) => (
                    <Link
                      key={client.id}
                      to={`/trainer/client/${client.id}`}
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-mustard-green-mid transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-medium text-mustard-charcoal">{client.candidate_name}</h3>
                        <span className="text-xs px-2 py-1 rounded bg-mustard-green-dark/10 text-mustard-green-dark font-medium">
                          {client.programme_type === 'lifespark360' ? 'LS360' : 'Seedbed'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-mustard-charcoal/60">
                        <span>Phase {client.current_phase} — {PHASE_LABELS[client.current_phase] || ''}</span>
                        <span>&middot;</span>
                        <span>Session {client.current_session}/{client.programme_type === 'lifespark360' ? 12 : 4}</span>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-mustard-green-dark h-2 rounded-full transition-all"
                          style={{ width: `${(client.current_session / (client.programme_type === 'lifespark360' ? 12 : 4)) * 100}%` }}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Completed */}
            {completed.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold text-mustard-charcoal/50 mb-4">Completed</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {completed.map((client) => (
                    <Link
                      key={client.id}
                      to={`/trainer/client/${client.id}`}
                      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <h3 className="font-medium text-mustard-charcoal">{client.candidate_name}</h3>
                      <p className="text-sm text-mustard-charcoal/50 mt-1">
                        {client.programme_type === 'lifespark360' ? 'LifeSpark360' : 'Seedbed'} — {client.package}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
